import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          body.append(key, value.toString());
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9onekqqs38sj6jc7c30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { /* not json */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const features = [
    { icon: 'ri-book-read-line', title: '海量资源', desc: '日漫韩漫国漫全覆盖，热门冷门一网打尽' },
    { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '流畅的下拉滚动体验，告别翻页烦恼' },
    { icon: 'ri-flashlight-line', title: '极速更新', desc: '漫画官方同步更新，追更零延迟' },
    { icon: 'ri-shield-check-line', title: '完全免费', desc: '无需注册，无需付费，打开即看' },
    { icon: 'ri-search-eye-line', title: '精准搜索', desc: '智能搜索，快速找到你想看的漫画' },
    { icon: 'ri-heart-line', title: '用户至上', desc: '不断优化阅读体验，为漫画爱好者而生' },
  ];

  return (
    <>
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于酷漫屋 - 免费漫画在线阅读平台',
            description: '酷漫屋是一个提供海量免费漫画资源的在线阅读平台，涵盖日漫、韩漫、国漫等多种类型，支持下拉式滚动阅读。',
            url: 'https://kumanwu.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '酷漫屋',
              url: 'https://kumanwu.com',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://kumanwu.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
                item: 'https://kumanwu.com/about',
              },
            ],
          }),
        }}
      />

      <Navbar />
      <main>
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="px-4 md:px-6 py-10 md:py-16 text-center" aria-label="品牌介绍">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4" id="brand">
            关于<strong>酷漫屋</strong>
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            酷漫屋是一个致力于为漫画爱好者提供优质阅读体验的<strong>免费漫画在线阅读平台</strong>。
            我们深知每一位漫画迷对作品的热爱与期待，因此酷漫屋汇集了海量<strong>日漫、韩漫、国漫</strong>资源，
            让您随时随地畅享<strong>免费看漫画</strong>的乐趣。
          </p>
        </section>

        {/* Features */}
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100" aria-label="平台特色" id="features">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              为什么选择酷漫屋
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="p-5 rounded-lg bg-background-50 border border-background-200/70 flex gap-4 items-start"
                >
                  <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${feature.icon} text-lg`}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{feature.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats & Reputation */}
        <section className="px-4 md:px-6 py-8 md:py-12" aria-label="平台数据">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              酷漫屋的信誉
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: '5000+', label: '漫画资源' },
                { value: '100万+', label: '月活用户' },
                { value: '99.9%', label: '可用率' },
                { value: '4.9', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100" aria-label="联系我们" id="contact">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议？欢迎随时联系我们
            </p>

            {/* Status Messages */}
            {formStatus === 'success' && (
              <div className="mb-6 p-4 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                感谢您的留言！我们会尽快回复您。
              </div>
            )}
            {formStatus === 'error' && formError && (
              <div className="mb-6 p-4 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                {formError}
              </div>
            )}

            <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-900 resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? '提交中...' : '发送留言'}
              </button>
            </form>

            {/* Contact Info */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-foreground-600">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-mail-line"></i>
                </span>
                <span>contact@kumanwu.com</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-wechat-line"></i>
                </span>
                <span>酷漫屋官方</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-weibo-line"></i>
                </span>
                <span>@酷漫屋漫画</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}